import React, { useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { format } from 'date-fns'
import { useI18n } from 'contexts/i18n'
import { groupsToNamedTypes } from 'utilities/query'
import queryString from 'query-string'
import { splitName } from 'utilities/file'
import { FileNameIcon, DownloadButton } from './s'
import Modal from 'modals/dialog'
import Form from 'components/form/controller'
import OneOfField from 'components/form/field/one-of'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { BackButton } from 'components/button'
import Paragraph from 'components/typography/paragraph'

const ThreeFormat = ({ previous, formData, salt }) => {
    const { formatMessage } = useIntl()
    const { dateLocale: locale } = useI18n()

    const [exportFormat, setExportFormat] = useState(exportFormats.xlsx)

    const {
        who,
        accountStatuses = 'active',
        includeSalary = false,
        salaryOnDate = null
    } = formData

    let entities = {}

    if(who === 'groups') {
        entities = {
            ...entities,
            ...groupsToNamedTypes(formData.groups, { ids: true })
        }
    }

    if(who === 'people') {
        entities.users = formData.people.map(({ id }) => id)
    }

    return (
        <Form layout="vertical">
            <Modal.Body>
                <OneOfField
                    name="exportFormat"
                    label={false}
                    field={{
                        value: exportFormat.extension,
                        include: 'always',
                        options: Object.values(exportFormats).map(({ extension, fileName, displayName, content: Content = null }) => ({
                            value: extension,
                            label: (
                                <FileNameIcon
                                    {...splitName(fileName)}
                                    displayName={formatMessage(displayName)}
                                    inline={true} />
                            ),
                            ...(Content ? { content: <Content /> } : null)
                        }))
                    }}
                    onChange={({ exportFormat }) => setExportFormat(exportFormats[exportFormat])}
                    salt={salt} />
                <Message
                    type="info"
                    className="preserve-line-breaks"
                    message={formatMessage({
                        id: 'people_export_information',
                        defaultMessage: 'All administrators will be notified about you exporting users and their data.\n\nHandle these files with care. They contain sensitive data about every employee.'
                    })} />
            </Modal.Body>
            <Modal.Footer>
                <Actions className="spread">
                    <BackButton onClick={previous} />
                    <DownloadButton
                        path={exportFormat.getDownloadPath(accountStatuses, includeSalary, salaryOnDate, entities)}
                        generateName={({ extension }) => {
                            const timestamp = format(new Date(), formatMessage({
                                id: 'date_fns_format_full_technical',
                                defaultMessage: `y-MM-dd 'at' HH:mm`
                            }), { locale })

                            return `Huma User Export ${timestamp}.${extension}`
                        }}
                        key={`${salt}:file:download:${exportFormat.extension}:${includeSalary ? 'with-salary' : 'without-salary'}:${salaryOnDate ?? 'no-date'}`} />
                </Actions>
            </Modal.Footer>
        </Form>
    )
}

const getIncludeActive = accountStatuses => {
    if(accountStatuses === 'active') {
        return { includeActive: true }
    }

    if(accountStatuses === 'inactive') {
        return { includeActive: false }
    }

    return null
}

const exportFormats = {
    xlsx: {
        extension: 'xlsx',
        fileName: 'Huma User Export.xlsx',
        displayName: {
            id: 'file_format_xlsx',
            defaultMessage: 'XLSX (Excel spreadsheet)'
        },
        getDownloadPath: (accountStatuses = 'active', includeSalary = false, salaryOnDate = null, entities = {}) => queryString.stringifyUrl({
            url: '/users/export/download',
            query: {
                format: 'XLSX',
                ...entities,
                ...getIncludeActive(accountStatuses),
                includeSalary,
                ...((!!includeSalary && salaryOnDate) ? { salaryOnDate } : null)
            }
        })
    },
    csv: {
        extension: 'csv',
        fileName: 'Huma User Export.csv',
        displayName: {
            id: 'file_format_csv',
            defaultMessage: 'CSV (comma-separated values)'

        },
        getDownloadPath: (accountStatuses = 'active', includeSalary = false, salaryOnDate = null, entities = {}) => queryString.stringifyUrl({
            url: '/users/export/download',
            query: {
                format: 'CSV',
                ...entities,
                ...getIncludeActive(accountStatuses),
                includeSalary,
                ...((!!includeSalary && salaryOnDate) ? { salaryOnDate } : null)
            }
        })
    },
    pax: {
        extension: 'pax',
        fileName: 'Huma User Export.pax',
        displayName: {
            id: 'file_format_pax',
            defaultMessage: 'XML (PAXml)'
        },
        getDownloadPath: (accountStatuses = 'active', includeSalary = false, salaryOnDate = null, entities = {}) => queryString.stringifyUrl({
            url: '/users/export/download',
            query: {
                format: 'PAX',
                ...entities,
                ...getIncludeActive(accountStatuses),
                includeSalary,
                ...((!!includeSalary && salaryOnDate) ? { salaryOnDate } : null)
            }
        }),
        content: () => (
            <>
                <Paragraph className="caption">
                    <FormattedMessage
                        id="export_pax_disclaimer"
                        defaultMessage="Users who have neither an employment ID nor a national identification number set, will be excluded from the export." />
                </Paragraph>
                <Paragraph className="caption compact">
                    <FormattedMessage
                        id="salary_export_pax_disclaimer"
                        defaultMessage="Salaries given in currencies other than SEK will also be excluded." />
                </Paragraph>
            </>
        )
    }
}

export default ThreeFormat